/**
 * Services de l'application : Supabase (ADR 0002), configuré par VITE_SUPABASE_URL et
 * VITE_SUPABASE_ANON_KEY. Les données ne sont jamais gardées dans le navigateur
 * (spécification §1 : synchronisées dans le cloud, pas de mode hors-ligne).
 *
 * Tests de bout en bout seulement : VITE_DEPOT_MEMOIRE=1 remplace Supabase par le dépôt en
 * mémoire (rien n'est enregistré, tout disparaît au rechargement). Cette branche est retirée
 * des versions normales à la construction.
 */
import { createClient } from '@supabase/supabase-js';
import { creerDepot, type Depot } from '../../supabase/depot';
import {
  creerAuthentificationSupabase,
  type Authentification,
  type Compte,
} from './authentification';

export interface Services {
  auth: Authentification;
  /** Dépôt de données du compte connecté. */
  depot(compte: Compte): Depot;
}

/** Services de test : dépôt et comptes en mémoire, perdus au rechargement de la page. */
async function servicesDeTest(): Promise<Services> {
  const [{ creerDepotMemoire }, { creerAuthentificationMemoire }] = await Promise.all([
    import('../../memoire/depot-memoire'),
    import('./authentification'),
  ]);
  const depots = new Map<string, Depot>();
  return {
    auth: creerAuthentificationMemoire(),
    depot(compte) {
      let depot = depots.get(compte.email);
      if (depot === undefined) {
        depot = creerDepotMemoire();
        depots.set(compte.email, depot);
      }
      return depot;
    },
  };
}

/** Services de l'application, ou `null` si le projet Supabase n'est pas configuré. */
export async function creerServices(): Promise<Services | null> {
  if (import.meta.env.VITE_DEPOT_MEMOIRE === '1') return servicesDeTest();
  const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  const cle = (import.meta.env.VITE_SUPABASE_ANON_KEY ??
    import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY) as string | undefined;
  if (!url || !cle) return null;
  const client = createClient(url, cle, {
    auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
  });
  const depot = creerDepot(client);
  return { auth: creerAuthentificationSupabase(client), depot: () => depot };
}
